import { useState, useEffect, useRef } from 'react'

export const BreakPoints = {
  screenM: 767,
  screenL: 1279,
} as const

/** Debounce delay in milliseconds */
const RESIZE_DEBOUNCE_MS = 100

interface WindowSize {
  width: number | undefined
  height: number | undefined
  isMobile: boolean | undefined
  isTablet: boolean | undefined
}

/**
 * useWindowSize - Hook for responsive breakpoint detection
 *
 * Returns current window dimensions and computed breakpoint flags.
 * Uses resize event listener with cleanup and debouncing for performance.
 *
 * @example
 * const { isMobile, isTablet } = useWindowSize()
 * const isCompact = isMobile || isTablet
 */
export function useWindowSize(): WindowSize {
  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: undefined,
    height: undefined,
    isMobile: undefined,
    isTablet: undefined,
  })
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const updateWindowSize = () => {
      const width = window.innerWidth
      setWindowSize({
        width,
        height: window.innerHeight,
        isMobile: width < BreakPoints.screenM,
        isTablet: width >= BreakPoints.screenM && width <= BreakPoints.screenL,
      })
    }

    const handleResize = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(updateWindowSize, RESIZE_DEBOUNCE_MS)
    }

    window.addEventListener('resize', handleResize)
    // Initial call without debounce
    updateWindowSize()

    return () => {
      window.removeEventListener('resize', handleResize)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return windowSize
}
